import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clamp } from 'lodash';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { inchesToFeetInches } from 'utils/math_util';
import { loadBlobAsImage } from 'utils/image_load';

import rooms from 'domains/wall_preview_rooms';
import ui from 'domains/ui';

import Spinner from 'components/shared/Spinner';

import type { EditUserRoomDraft, WallPreviewUserRoom } from 'domains/wall_preview_rooms';
import type { FeetInchesPair } from 'utils/math_util';

type EditRoomUploadProps = {
  room: WallPreviewUserRoom;
  updateCalculatedWidthDisplay: (
    should: boolean,
    current: FeetInchesPair,
    update: FeetInchesPair
  ) => void;
  handleChangeRoom: () => void;
};

function EditRoomUpload(props: EditRoomUploadProps) {
  const { room, updateCalculatedWidthDisplay, handleChangeRoom } = props;

  const dispatch = useAppDispatch();
  const previousEdit = useAppSelector((state) => rooms.selectors.editRoomDraftById(state, room.id));

  const [isLoading, setIsLoading] = useState(true);

  // Rendered image size (not full res)
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  // Left(1) and right(2) measurement handle selection & positions
  const [whichHandle, setWhichHandle] = useState<0 | 1 | 2>(0);

  // Init the coordinates to the 'previous' value so we can use the paginated modal
  const [x1, setX1] = useState(() => (previousEdit && previousEdit.x1 ? previousEdit.x1 : 0));
  const [y1, setY1] = useState(() => (previousEdit && previousEdit.y1 ? previousEdit.y1 : 0));
  const [x2, setX2] = useState(() => (previousEdit && previousEdit.x2 ? previousEdit.x2 : 0));
  const [y2, setY2] = useState(() => (previousEdit && previousEdit.y2 ? previousEdit.y2 : 0));

  // How much to scale the full res image for zooming
  const [imageScale, setImageScale] = useState(0);
  // Pixel measurement of the drawn measurement line
  const [hypotenuse, setHypotenuse] = useState(0);
  // User entered value for the measurement
  const [lengthInput, setLengthInput] = useState(() => {
    return previousEdit && previousEdit.lengthInput ? previousEdit.lengthInput : 60;
  });
  // Calculated width of the room in inches
  const [calculatedWidth, setCalculatedWidth] = useState(room.wall_size);
  // User's description
  const [description, setDescription] = useState(room.description);
  // Set when a user makes an action to change the width -- moving a point or changing the length input
  const [shouldUpdateWidth, setShouldUpdateWidth] = useState(() => {
    return previousEdit && previousEdit.shouldUpdateWidth ? previousEdit.shouldUpdateWidth : false;
  });

  const handle1 = useRef({ x: 0, y: 0 });
  const handle2 = useRef({ x: 0, y: 0 });

  function setHandle1(x: number, y: number) {
    handle1.current.x = x;
    handle1.current.y = y;
    setX1(handle1.current.x);
    setY1(handle1.current.y);
  }

  function setHandle2(x: number, y: number) {
    handle2.current.x = x;
    handle2.current.y = y;
    setX2(handle2.current.x);
    setY2(handle2.current.y);
  }

  // Handles setting the x & y of whichever handle is being dragged
  function handleButtonDrag(event: React.MouseEvent<HTMLButtonElement>, handle: 1 | 2) {
    let positionSetter: (x: number, y: number) => void;
    let restrictY: number;

    if (handle === 1) {
      positionSetter = setHandle1;
      restrictY = y2;
    } else if (handle === 2) {
      positionSetter = setHandle2;
      restrictY = y1;
    } else {
      // Don't know why typescript can't infer that positionSetter will be set
      // even though it can infer restrictY, but it doesn't so this clears it up
      return;
    }

    const imageContainer = document.getElementById('edit-room-image');
    if (!imageContainer) return;

    const { x: imageX, y: imageY } = imageContainer.getBoundingClientRect();

    // Set the zoom & handle position data on click
    handleZoom(event.clientX - imageX, event.clientY - imageY);
    positionSetter(event.clientX - imageX, event.clientY - imageY);
    setWhichHandle(handle);

    setShouldUpdateWidth(true);

    function trackmove(e: MouseEvent) {
      const { clientX, clientY, shiftKey } = e;
      const x = clamp(clientX - imageX, 0, imageWidth);
      const y = shiftKey ? restrictY : clamp(clientY - imageY, 0, imageHeight);
      handleZoom(x, y);
      positionSetter(x, y);
    }

    document.addEventListener('mousemove', trackmove);
    document.addEventListener(
      'mouseup',
      () => {
        setWhichHandle(0);
        document.removeEventListener('mousemove', trackmove);
      },
      { once: true }
    );
  }

  function handleButtonDragTouch(startEvent: React.TouchEvent<HTMLButtonElement>, handle: 1 | 2) {
    startEvent.preventDefault();

    let positionSetter: (x: number, y: number) => void;
    let restrictY: number;

    if (handle === 1) {
      positionSetter = setHandle1;
      restrictY = y2;
    } else if (handle === 2) {
      positionSetter = setHandle2;
      restrictY = y1;
    } else {
      // Don't know why typescript can't infer that positionSetter will be set
      // even though it can infer restrictY, but it doesn't so this clears it up
      return;
    }

    const imageContainer = document.getElementById('edit-room-image');
    if (!imageContainer) return;

    const { x: imageX, y: imageY } = imageContainer.getBoundingClientRect();

    // Set the zoom & handle position data on click
    const startTouch = startEvent.touches[0];
    handleZoom(startTouch.clientX - imageX, startTouch.clientY - imageY);
    positionSetter(startTouch.clientX - imageX, startTouch.clientY - imageY);
    setWhichHandle(handle);

    setShouldUpdateWidth(true);

    const trackmove = (e: TouchEvent) => {
      e.preventDefault();
      const { touches, shiftKey } = e;
      const { clientX, clientY } = touches[0];
      const x = clamp(clientX - imageX, 0, imageWidth);
      const y = shiftKey ? restrictY : clamp(clientY - imageY, 0, imageHeight);
      handleZoom(x, y);
      positionSetter(x, y);
    };

    document.addEventListener('touchmove', trackmove, { passive: false });
    document.addEventListener(
      'touchend',
      () => {
        setWhichHandle(0);
        document.removeEventListener('touchmove', trackmove);
      },
      { once: true }
    );
  }

  // Handles slicing a portion out of a full-res image from one canvas and
  //  placing it in a second 'zoom' canvas
  function handleZoom(x: number, y: number) {
    const canvas = document.getElementById('edit-room-image-canvas') as HTMLCanvasElement | null;
    const canvas2 = document.getElementById('edit-room-zoom-canvas') as HTMLCanvasElement | null;

    const context = canvas2?.getContext('2d');
    if (!context || !canvas) return;

    // Cribbed & slightly modified from MDN
    // https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Pixel_manipulation_with_canvas
    const zoom = function zoom(ctx: CanvasRenderingContext2D, zoomX: number, zoomY: number) {
      // Scaling function so we can use the full res image for zooming
      const scaled = (n: number) => n * imageScale;

      // How many pixels of the visible image to zoom
      const pixelsToZoom = 5;
      const zoomFrame = pixelsToZoom * 2;

      // Scale these once
      const scaledPixels = scaled(pixelsToZoom);
      const scaledFrame = scaled(zoomFrame);

      // x and y coordinates to use from the full res canvas
      // Move left & up from the drag handle's position by pixelsToZoom, but bounded by image size
      const sourceX = Math.min(
        Math.max(0, scaled(zoomX) - scaledPixels),
        scaled(imageWidth) - scaledFrame
      );
      const sourceY = Math.min(
        Math.max(0, scaled(zoomY) - scaledPixels),
        scaled(imageHeight) - scaledFrame
      );

      // Draw the zoomed image in the second canvas
      ctx.drawImage(
        canvas,
        sourceX,
        sourceY, // full res canvas coordinate for starting the slice
        scaledFrame,
        scaledFrame, // how many pixels of the full res canvas to slice
        0,
        0, // place the image at the top left of the zoom canvas
        100,
        100 // make the zoom canvas 100px square (we round corners in CSS)
      );

      // Draw the target cross inside the zoom canvas
      ctx.beginPath();
      ctx.moveTo(25, 50);
      ctx.lineTo(75, 50);
      ctx.moveTo(50, 25);
      ctx.lineTo(50, 75);
      ctx.closePath();
      ctx.stroke();
    };

    zoom(context, x, y);
  }

  function setImageSize(e: React.SyntheticEvent<HTMLImageElement>) {
    const { clientHeight, clientWidth } = e.target as HTMLImageElement;
    setImageWidth(clientWidth);
    setImageHeight(clientHeight);
    // Set the starting points for the handles
    if (!previousEdit || !previousEdit.x1) {
      setHandle1(0.25 * clientWidth, 0.5 * clientHeight);
      setHandle2(0.75 * clientWidth, 0.5 * clientHeight);
    }
  }

  // Create imaginary right triangle to use for the measurement ratio calculation
  useEffect(() => {
    // horizontal edge
    const a = Math.abs(x1 - x2);
    // vertical edge
    const b = Math.abs(y1 - y2);

    // a^2 times b^2 equals c^2
    const hypotenuseToSet = Math.sqrt(a ** 2 + b ** 2);
    setHypotenuse(hypotenuseToSet);
  }, [x1, y1, x2, y2]);

  // Use the imaginary right triangle to create a ratio and convert user's measurement to the whole image
  // Image Width (px) : Hypotenuse (px) :: Room Width (in) : User Input Length (in)
  useEffect(() => {
    if (hypotenuse !== 0) {
      const x = (imageWidth / hypotenuse) * lengthInput;
      setCalculatedWidth(x);
    }
  }, [hypotenuse, lengthInput, imageWidth]);

  // Init the zoom source canvas
  useEffect(() => {
    async function initZoomCanvas() {
      const canvas = document.getElementById('edit-room-image-canvas') as HTMLCanvasElement | null;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      try {
        const image = await loadBlobAsImage(room.url);
        const { naturalHeight, naturalWidth } = image;
        setIsLoading(false);
        setImageScale(naturalWidth / imageWidth);
        ctx.drawImage(image, 0, 0, naturalWidth, naturalHeight);
      } catch (error) {
        dispatch(
          ui.actions.setSiteMessage({
            messageType: 'error',
            message: 'Failed to load image. Refresh the page and try again.',
          })
        );
        dispatch(rooms.actions.resetEditDrafts());
        handleChangeRoom();
      }
    }

    if (room.url && imageWidth && imageHeight) {
      initZoomCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, imageWidth, imageHeight, dispatch]);

  useEffect(() => {
    let update: EditUserRoomDraft = {
      id: room.id,
      wallSize: calculatedWidth,
      description,
      lengthInput,
      shouldUpdateWidth,
    };

    if (x1 !== 0 && x2 !== 0 && y1 !== 0 && y2 !== 0) {
      update = { ...update, x1, x2, y1, y2 };
    }

    dispatch(rooms.actions.updateRoomEditDraft({ update, id: room.id }));
  }, [
    x1,
    y1,
    x2,
    y2,
    lengthInput,
    description,
    calculatedWidth,
    shouldUpdateWidth,
    room,
    dispatch,
  ]);

  // styles for the 'left' button
  const handle1Style: React.CSSProperties = {
    left: `${x1 - 10}px`,
    top: `${y1 - 10}px`,
  };

  // styles for the 'right' button
  const handle2Style: React.CSSProperties = {
    left: `${x2 - 10}px`,
    top: `${y2 - 10}px`,
  };

  // Styles for the magnified canvas
  const zoomCanvasStyle: React.CSSProperties = useMemo(() => {
    let left = 0;
    let top = 0;
    let display = 'block';

    switch (whichHandle) {
      case 1:
        left = x1;
        top = y1;
        break;
      case 2:
        left = x2;
        top = y2;
        break;
      case 0:
      default:
        display = 'none';
    }
    return {
      border: '1px solid #00a7e1',
      borderRadius: '100px',
      position: 'absolute',
      zIndex: 3,
      display,
      left,
      top,
    };
  }, [whichHandle, x1, x2, y1, y2]);

  const spinnerStyle: React.CSSProperties = {
    bottom: '50%',
    height: '50px',
    left: '0',
    margin: 'auto',
    position: 'absolute',
    right: '0',
    top: '0',
    width: '50px',
  };

  const imageBoundingStyle: React.CSSProperties = useMemo(() => {
    return {
      height: imageHeight,
      width: imageWidth,
    };
  }, [imageHeight, imageWidth]);

  useEffect(() => {
    const currentWidthDisplay = inchesToFeetInches(room.wall_size);
    const updatedWidthDisplay = inchesToFeetInches(calculatedWidth);
    updateCalculatedWidthDisplay(shouldUpdateWidth, currentWidthDisplay, updatedWidthDisplay);
  }, [shouldUpdateWidth, room, calculatedWidth, updateCalculatedWidthDisplay]);

  return (
    <React.Fragment>
      {isLoading && <Spinner color="black" size="50" style={spinnerStyle} />}
      <div className={`edit-room-upload ${isLoading ? 'loading' : ''}`}>
        <div className="edit-room-canvas-container" style={imageBoundingStyle}>
          <canvas
            id="edit-room-image-canvas"
            width={imageWidth * imageScale}
            height={imageHeight * imageScale}
          />
          <canvas id="edit-room-zoom-canvas" style={zoomCanvasStyle} width={100} height={100} />

          <svg className="measurement-line" viewBox={`0 0 ${imageWidth} ${imageHeight}`}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="black"
              strokeDasharray="6 3"
              strokeWidth="2"
            />
          </svg>

          <img onLoad={setImageSize} id="edit-room-image" src={room.url} alt={room.description} />

          <div className="edit-room-handle-container" style={imageBoundingStyle}>
            <button
              className="drag-handle"
              onMouseDown={(e) => handleButtonDrag(e, 1)}
              onTouchStart={(e) => handleButtonDragTouch(e, 1)}
              style={handle1Style}
            >
              <span aria-hidden="true">+</span>
              <span className="screen-reader-text">Drag handle left side</span>
            </button>
            <button
              className="drag-handle"
              onMouseDown={(e) => handleButtonDrag(e, 2)}
              onTouchStart={(e) => handleButtonDragTouch(e, 2)}
              style={handle2Style}
            >
              <span aria-hidden="true">+</span>
              <span className="screen-reader-text">Drag handle right side</span>
            </button>
          </div>
        </div>

        <div className="edit-guide">
          <div>
            <FontAwesomeIcon className="edit-guide-icon" icon="ruler-combined" size="2x" />
            <p>Measure an object against the wall in your room</p>
          </div>
          <span>
            <FontAwesomeIcon icon="angle-right" size="3x" />
          </span>
          <div>
            <div className="measurement-line-icon">
              <svg viewBox="0 0 100 20">
                <circle cx="5" cy="10" r="4" strokeWidth="2" />
                <line x1={10} y1={10} x2={55} y2={10} strokeDasharray="6 3" strokeWidth="2" />
                <circle cx="60" cy="10" r="4" strokeWidth="2" />
              </svg>
            </div>
            <p>
              Adjust the width of the line in the uploaded photo to match the object you measured
            </p>
          </div>
          <span>
            <FontAwesomeIcon icon="angle-right" size="3x" />
          </span>
          <div className="input-group">
            <label htmlFor="length-measurement">Enter the width of the object in inches</label>
            <input
              id="length-measurement"
              type="number"
              value={lengthInput}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setLengthInput(value);
                setShouldUpdateWidth(true);
              }}
            />
          </div>
          <span>
            <FontAwesomeIcon icon="angle-right" size="3x" />
          </span>
          <div className="input-group">
            <label htmlFor="room-name">Describe your room (ie Bedroom, Living Room)</label>
            <input
              type="text"
              id="room-name"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default EditRoomUpload;
